// A product's details (P1, decision 138): the box as bought, where it comes from by season, the vendors that priced
// it, and seasonality from our own numbers. Prices live on Price management; nothing here sets a price.
import type { Item, ItemProfile, ItemSource } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { formatDate, formatMoney, formatQty, parseTyped } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { UsdaCard } from './UsdaCard';
import { ErrorBox } from './ui';

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const monthName = (m: number, intlLocale: string, style: 'short' | 'narrow' = 'short') => new Intl.DateTimeFormat(intlLocale, { month: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2026, m - 1, 15)));
const thisMonth = () => Number(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Denver', month: 'numeric' }).format(new Date()));

/** "Nov–Apr", "Jun, Aug": runs of months written as ranges, wrapping over the new year. */
export function monthsText(months: number[], intlLocale: string): string {
  const set = new Set(months);
  if (set.size === 12) return `${monthName(1, intlLocale)}–${monthName(12, intlLocale)}`;
  // start a run at a month whose previous month is not in the set
  const starts = MONTHS.filter((m) => set.has(m) && !set.has(m === 1 ? 12 : m - 1));
  return starts
    .map((start) => {
      let end = start;
      while (set.has(end === 12 ? 1 : end + 1) && (end === 12 ? 1 : end + 1) !== start) end = end === 12 ? 1 : end + 1;
      return start === end ? monthName(start, intlLocale) : `${monthName(start, intlLocale)}–${monthName(end, intlLocale)}`;
    })
    .join(', ');
}

/** A box weight as typed: a positive number of pounds, else null (an empty field is null too). */
const weightOf = (raw: string): number | null => {
  const n = parseTyped(raw);
  return n !== null && n > 0 && n <= 9999 ? n : null;
};
/** Pieces in a box: a positive whole number, never rounded from a decimal. */
const countOf = (raw: string): number | null => {
  const n = parseTyped(raw);
  return n !== null && Number.isInteger(n) && n > 0 && n <= 99999 ? n : null;
};

type Draft = { boxWeightLb: string; boxCount: string; sources: (ItemSource & { key: string })[] };
let seq = 0;

export function ItemProfileSections({ item, editable }: { item: Item; editable: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const profile = useQuery({ queryKey: qk.itemProfile(item.id), queryFn: ({ signal }) => api.itemProfile(item.id, signal) });
  const vendors = useQuery({ queryKey: ['vendors'], queryFn: ({ signal }) => api.vendors(signal), enabled: editable });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: (d: Draft) =>
      api.saveItemProfile(item.id, {
        boxWeightLb: weightOf(d.boxWeightLb),
        boxCount: countOf(d.boxCount),
        // a row left completely blank is no row; a half-filled one never gets here (see problems)
        sources: d.sources.filter((s) => s.region.trim() || s.months.length).map(({ key: _k, vendorName: _v, ...s }) => s),
      }),
    onSuccess: (p) => {
      qc.setQueryData(qk.itemProfile(item.id), p);
      // the list's box and "comes from now" columns; the details just saved are already in place
      void qc.invalidateQueries({ queryKey: ['items', 'list'] });
      setDraft(null);
      setTried(false);
    },
  });
  // what cannot be saved as typed: shown in red, and Save waits (nothing typed is thrown away without a word)
  const badWeight = !!draft && draft.boxWeightLb.trim() !== '' && weightOf(draft.boxWeightLb) === null;
  const badCount = !!draft && draft.boxCount.trim() !== '' && countOf(draft.boxCount) === null;
  const halfRows = new Set((draft?.sources ?? []).filter((s) => (s.region.trim() !== '') !== (s.months.length > 0)).map((s) => s.key));
  const problems = badWeight || badCount || halfRows.size > 0;
  const p = profile.data;
  const now = thisMonth();

  const startEdit = (x: ItemProfile) =>
    setDraft({
      boxWeightLb: x.boxWeightLb == null ? '' : String(x.boxWeightLb),
      boxCount: x.boxCount == null ? '' : String(x.boxCount),
      sources: x.sources.map((s) => ({ ...s, key: `s${(seq += 1)}` })),
    });
  const setSource = (key: string, change: Partial<ItemSource>) => setDraft((d) => (d ? { ...d, sources: d.sources.map((s) => (s.key === key ? { ...s, ...change } : s)) } : d));

  if (profile.error) return <ErrorBox error={profile.error} />;
  if (!p) return <p className="muted">{t('loading')}</p>;

  // the bar is cases only: register units are each or pounds and are not added to cases
  const peak = Math.max(1, ...p.seasonality.map((s) => s.soldPerYear));
  const hasSales = p.seasonality.some((s) => s.soldPerYear > 0 || s.retailPerYear > 0);

  return (
    <>
      <section className="card stack-sm" aria-labelledby="box-h">
        <div className="card-head">
          <h2 id="box-h">{t('boxBlock')}</h2>
          {editable && !draft ? (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => startEdit(p)}>
              {t('edit')}
            </button>
          ) : null}
        </div>
        {draft ? (
          <div className="two-up">
            <label className="field">
              <span>{t('boxWeight')}</span>
              <input inputMode="decimal" className={badWeight ? 'bad' : undefined} aria-invalid={badWeight || undefined} value={draft.boxWeightLb} onChange={(e) => setDraft({ ...draft, boxWeightLb: e.target.value })} />
              {badWeight ? <span className="field-error">{t('boxWeightBad')}</span> : null}
            </label>
            <label className="field">
              <span>{t('boxCount')}</span>
              <input inputMode="numeric" className={badCount ? 'bad' : undefined} aria-invalid={badCount || undefined} value={draft.boxCount} onChange={(e) => setDraft({ ...draft, boxCount: e.target.value })} />
              {badCount ? <span className="field-error">{t('boxCountBad')}</span> : null}
            </label>
          </div>
        ) : (
          <dl className="facts plain">
            <div className="fact">
              <dt>{t('boxWeight')}</dt>
              <dd>{p.boxWeightLb != null ? formatQty(p.boxWeightLb) : '—'}</dd>
            </div>
            <div className="fact">
              <dt>{t('boxCount')}</dt>
              <dd>{p.boxCount ?? '—'}</dd>
            </div>
            <div className="fact">
              <dt>{t('content')}</dt>
              <dd>{item.content && item.contentUnit ? `${item.content} ${item.contentUnit}` : '—'}</dd>
            </div>
          </dl>
        )}
      </section>

      <section className="card stack-sm" aria-labelledby="src-h">
        <h2 id="src-h">{t('sourcingBlock')}</h2>
        <p className="muted">{t('sourcingHint')}</p>
        {draft ? (
          <div className="stack-sm">
            {draft.sources.map((s) => (
              <div key={s.key} className={halfRows.has(s.key) && tried ? 'source-edit bad' : 'source-edit'}>
                {halfRows.has(s.key) && tried ? <p className="field-error">{t('sourceHalf')}</p> : null}
                <div className="two-up">
                  <label className="field">
                    <span>{t('regionLabel')}</span>
                    <input value={s.region} placeholder={t('regionPlaceholder')} maxLength={80} onChange={(e) => setSource(s.key, { region: e.target.value })} />
                  </label>
                  <label className="field">
                    <span>{t('sourceVendor')}</span>
                    <select value={s.vendorId ?? ''} onChange={(e) => setSource(s.key, { vendorId: e.target.value || null })}>
                      <option value="">{t('anyVendor')}</option>
                      {(vendors.data ?? []).map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="field">
                  <span>{t('monthsLabel')}</span>
                  <div className="month-pick" role="group" aria-label={t('monthsLabel')}>
                    {MONTHS.map((m) => {
                      const on = s.months.includes(m);
                      return (
                        <button key={m} type="button" className={on ? 'chip on' : 'chip'} aria-pressed={on} onClick={() => setSource(s.key, { months: on ? s.months.filter((x) => x !== m) : [...s.months, m].sort((a, b) => a - b) })}>
                          {monthName(m, intlLocale)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="row-actions">
                  <label className="field grow">
                    <span>{t('sourceNote')}</span>
                    <input value={s.note ?? ''} maxLength={200} onChange={(e) => setSource(s.key, { note: e.target.value || null })} />
                  </label>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft({ ...draft, sources: draft.sources.filter((x) => x.key !== s.key) })}>
                    {t('remove')}
                  </button>
                </div>
              </div>
            ))}
            {draft.sources.length < 12 ? (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDraft({ ...draft, sources: [...draft.sources, { key: `s${(seq += 1)}`, region: '', months: [], vendorId: null, note: null }] })}>
                {t('addSource')}
              </button>
            ) : null}
            {save.error ? <ErrorBox error={save.error} /> : null}
            <div className="row-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setDraft(null)} disabled={save.isPending}>
                {t('cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setTried(true);
                  if (!problems) save.mutate(draft);
                }}
                disabled={save.isPending || badWeight || badCount}
              >
                {save.isPending ? t('sending') : t('save')}
              </button>
            </div>
          </div>
        ) : p.sources.length === 0 ? (
          <p className="muted">{t('sourcingNone')}</p>
        ) : (
          <ul className="list">
            {p.sources.map((s) => (
              <li key={s.id ?? s.region} className={s.months.includes(now) ? 'row current' : 'row'}>
                <span className="row-main">
                  <span className="row-title">{s.region}</span>
                  <span className="row-sub">
                    {monthsText(s.months, intlLocale)}
                    {s.vendorName ? ` · ${s.vendorName}` : ''}
                    {s.note ? ` · ${s.note}` : ''}
                  </span>
                </span>
                {s.months.includes(now) ? <span className="badge badge-ok">{t('originNow')}</span> : null}
              </li>
            ))}
          </ul>
        )}
        <h3 className="label">{t('vendorsPriced')}</h3>
        {p.vendors.length === 0 ? (
          <p className="muted">{t('vendorsNone')}</p>
        ) : (
          <ul className="list">
            {p.vendors.map((v) => (
              <li key={v.vendorId} className="row">
                <span className="row-main">
                  <span className="row-title">{v.vendorName}</span>
                  <span className="row-sub">{v.prices === 1 ? t('priceOne') : t('pricesCount').replace('{n}', String(v.prices))}</span>
                </span>
                <span className="row-end">
                  <span className="row-end-label">{formatDate(v.lastDate, intlLocale, 'date')}</span>
                  {v.lastPrice != null ? (
                    <span className="row-end-value">
                      {formatMoney(v.lastPrice, intlLocale)}
                      {v.unit ? ` / ${v.unit}` : ''}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card stack-sm" aria-labelledby="season-h">
        <h2 id="season-h">{t('seasonBlock')}</h2>
        <p className="muted">{t('seasonHint')}</p>
        {!hasSales && p.seasonality.every((s) => s.avgCost === null) ? (
          <p className="muted">{t('seasonNoData')}</p>
        ) : (
          <div className="table-scroll">
            <table className="season" aria-label={t('seasonBlock')}>
              <thead>
                <tr>
                  <th scope="col">{t('monthsLabel')}</th>
                  <th scope="col" className="num">
                    {t('seasonSold')} ({item.packUnit})
                  </th>
                  <th scope="col" className="num">{t('seasonRetail')}</th>
                  <th scope="col" className="num">{t('seasonPrice')}</th>
                  <th scope="col" className="num">{t('seasonCost')}</th>
                </tr>
              </thead>
              <tbody>
                {p.seasonality.map((s) => (
                  <tr key={s.month} className={s.month === now ? 'current' : undefined}>
                    <th scope="row">{monthName(s.month, intlLocale)}</th>
                    <td className="num season-bar-cell">
                      <span className="season-bar" style={{ width: `${Math.round((s.soldPerYear / peak) * 100)}%` }} aria-hidden="true" />
                      <span className="season-val">{s.soldPerYear ? formatQty(s.soldPerYear) : '—'}</span>
                    </td>
                    <td className="num">{s.retailPerYear ? formatQty(s.retailPerYear) : '—'}</td>
                    <td className="num">{s.avgPrice != null ? formatMoney(s.avgPrice, intlLocale) : '—'}</td>
                    <td className="num">{s.avgCost != null ? formatMoney(s.avgCost, intlLocale) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <UsdaCard itemId={item.id} editable={editable} />
    </>
  );
}
